import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import ChartCard from './ChartCard.tsx';
import Chart from './Chart.tsx';
import LegendItem from './LegendItem.tsx';
import Stats from '../../core/components/Stats.tsx';
import TooltipContent from './TooltipContent.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { withIcon } from '../../core/components/Icon';
import { faArrowTrendDown, faArrowTrendUp } from '@fortawesome/pro-regular-svg-icons';
import { addMonths, differenceInDays, format, isValid, parse, parseISO, subDays } from 'date-fns';
import { isoDateFormat } from '../../core/utils/dates.ts';
import FilterDropdown, { FilterGroup } from '../../core/components/Filter.tsx';
import { formatDate, formatDateFull } from '../utils/chartUtils.ts';
import {
    SmartInboxIntegrationOption,
    useSmartInboxEmailCategories,
    useSmartInboxEmailCategoriesOverTime,
    useSmartInboxIntegrations,
    useSmartInboxPerformanceMetrics,
    useSmartInboxShare,
    useSmartInboxShareOverTime,
    useSmartInboxTopEmailCategories,
} from '../utils/dataByDocumentType.ts';

const TrendUpIcon = withIcon(faArrowTrendUp);
const TrendDownIcon = withIcon(faArrowTrendDown);

const COLOR_KEYS = ['data1', 'data2', 'data3', 'data4'] as const;
type ColorKey = (typeof COLOR_KEYS)[number];

const DATA_CSS_COLORS: Record<ColorKey, string> = {
    data1: 'var(--color-data-data1)',
    data2: 'var(--color-data-data2)',
    data3: 'var(--color-data-data3)',
    data4: 'var(--color-data-data4)',
};

const CATEGORY_COLOR_PALETTE = [
    'var(--color-data-data1)',
    'var(--color-data-data2)',
    'var(--color-data-data3)',
    'var(--color-data-data4)',
    '#fdf087',
    '#fb7185',
    '#99e9b3',
    '#22d3ee',
    '#fbbf24',
];

const generateCategoryColor = (index: number): string => CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length];

const formatTrend = (
    value: number | null | undefined,
    inverted = false
): { label: string; direction: string | null; positive: boolean | null } | null => {
    if (value === null || value === undefined) return null;

    const rounded = Math.round(Math.abs(value));
    if (rounded === 0) return { label: '0%', direction: null, positive: null };

    return {
        label: `${rounded}%`,
        direction: value > 0 ? 'up' : 'down',
        positive: inverted ? value < 0 : value > 0,
    };
};

const TrendBadge = ({
    value,
    direction,
    positive,
}: {
    value: string;
    direction: string | null;
    positive: boolean | null;
}) => (
    <span
        className={classnames(
            'text-xs font-medium p-1 w-fit',
            positive === true
                ? 'text-content-success-strong bg-surface-success-weak'
                : positive === false
                  ? 'text-content-danger-strong bg-surface-danger-weak'
                  : 'text-content-medium bg-surface-weak-default'
        )}
    >
        {direction === 'up' ? (
            <TrendUpIcon className="pr-1" />
        ) : direction === 'down' ? (
            <TrendDownIcon className="pr-1" />
        ) : null}{' '}
        {value}
    </span>
);

const MetricCard = ({
    title,
    helpText,
    loading,
    value,
    unit,
    trend,
    trendDirection,
    trendPositive,
    sinceLabel,
    className,
}: {
    title: string;
    helpText?: string;
    loading?: boolean;
    value: string | number;
    unit?: string;
    trend?: string;
    trendDirection?: string | null;
    trendPositive?: boolean | null;
    sinceLabel?: string;
    className?: string;
}) => {
    const { t, i18n } = useTranslation('analytics');
    return (
        <ChartCard
            title={title}
            helpText={helpText}
            loading={loading}
            className={classnames('min-h-64 gap-[78px] pt-6 px-6 pb-[78px]', className)}
        >
            <div className="flex flex-col gap-1 w-full">
                <div className="flex items-baseline gap-1">
                    <span className="font-medium text-4xl text-content-strong">
                        {typeof value === 'number' ? value.toLocaleString(i18n.language) : value}
                    </span>
                    {unit && <span className="font-normal text-content-medium text-2xl">{unit}</span>}
                </div>
                {trend && trendDirection !== undefined && (
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex flex-row gap-1">
                            <TrendBadge value={trend} direction={trendDirection} positive={trendPositive ?? null} />
                            {sinceLabel && (
                                <span className="self-center text-sm text-content-medium">{sinceLabel}</span>
                            )}
                        </div>
                        <span className="text-xs text-content-medium">{t('dashboard.inbox.vsSelected')}</span>
                    </div>
                )}
            </div>
        </ChartCard>
    );
};

const PerformanceMetricCard = ({
    title,
    helpText,
    loading,
    value,
    unit,
    trend,
    trendDirection,
    trendPositive,
    description,
}: {
    title: string;
    helpText?: string;
    loading?: boolean;
    value: string | number;
    unit?: string;
    trend?: string;
    trendDirection?: 'up' | 'down' | null;
    trendPositive?: boolean | null;
    description?: React.ReactNode;
}) => {
    const { t } = useTranslation('analytics');
    return (
        <ChartCard title={title} helpText={helpText} loading={loading} className="min-h-64 pt-6 px-6 pb-5 gap-12">
            <div className="flex flex-col w-full">
                <div className="flex items-baseline gap-1">
                    <span className="font-medium text-4xl text-content-strong">{value}</span>
                    {unit && <span className="font-normal text-content-medium text-xl">{unit}</span>}
                </div>
                {trend && trendDirection !== undefined && (
                    <div className="flex flex-col gap-1 mt-3">
                        <TrendBadge value={trend} direction={trendDirection} positive={trendPositive ?? null} />
                        <span className="text-xs text-content-medium">{t('dashboard.inbox.vsSelected')}</span>
                    </div>
                )}
                {description && (
                    <p className="text-xs mt-12 text-content-strong leading-relaxed [&_strong]:text-content-strong [&_strong]:font-medium">
                        {description}
                    </p>
                )}
            </div>
        </ChartCard>
    );
};

const MONTHLY_THRESHOLD_DAYS = 60;

const safeParseDate = (key: string): Date | null => {
    if (!key) return null;
    const d = parseISO(key);
    if (isValid(d)) return d;
    const d2 = parse(key, isoDateFormat, new Date());
    return isValid(d2) ? d2 : null;
};

const getAllMonthsInRange = (startDate: string, endDate: string): string[] => {
    const start = safeParseDate(startDate);
    const end = safeParseDate(endDate);
    if (!start || !end) return [];
    const months: string[] = [];
    const firstFullMonth = start.getDate() === 1 ? start : new Date(start.getFullYear(), start.getMonth() + 1, 1);
    let current = new Date(firstFullMonth.getFullYear(), firstFullMonth.getMonth(), 1);
    const strictEnd = start.getDate() === 1 && end.getDate() === 1;
    while (strictEnd ? current < end : current <= end) {
        months.push(format(current, 'yyyy-MM'));
        current = addMonths(current, 1);
    }
    return months;
};

const aggregateByMonth = (data: { date: string; smartInboxCount: number; nonSmartInboxCount: number }[]) => {
    const byMonth: Record<string, { date: string; smartInboxCount: number; nonSmartInboxCount: number }> = {};
    for (const row of data) {
        const d = safeParseDate(row.date);
        if (!d) continue;
        const key = format(d, 'yyyy-MM');
        if (!byMonth[key]) byMonth[key] = { date: key, smartInboxCount: 0, nonSmartInboxCount: 0 };
        byMonth[key].smartInboxCount += row.smartInboxCount;
        byMonth[key].nonSmartInboxCount += row.nonSmartInboxCount;
    }
    return Object.values(byMonth);
};

const EmailDistributionChart = ({
    data,
    dateRange,
}: {
    data: { date: string; smartInboxCount: number; nonSmartInboxCount: number }[];
    dateRange: string[];
}) => {
    const barSize = 28;
    const { t, i18n } = useTranslation('analytics');
    const isMonthly = useMemo(() => {
        const [startDate, endDate] = dateRange;
        const start = safeParseDate(startDate);
        const end = safeParseDate(endDate);
        if (!start || !end) return false;
        return differenceInDays(end, start) > MONTHLY_THRESHOLD_DAYS;
    }, [dateRange]);

    const chartData = useMemo(() => {
        if (isMonthly) {
            const byMonth = new Map(aggregateByMonth(data).map((r) => [r.date, r]));
            return getAllMonthsInRange(dateRange[0], dateRange[1]).map((key) => {
                const r = byMonth.get(key) ?? { date: key, smartInboxCount: 0, nonSmartInboxCount: 0 };
                return {
                    ...r,
                    date: formatDate(i18n.language, key, true),
                    tooltipDate: formatDateFull(i18n.language, key, true),
                };
            });
        }
        return data
            .filter((r) => r.smartInboxCount > 0 || r.nonSmartInboxCount > 0)
            .map((r) => ({
                ...r,
                date: formatDate(i18n.language, r.date, false),
                tooltipDate: formatDateFull(i18n.language, r.date, false),
            }));
    }, [data, isMonthly, i18n.language, dateRange]);

    return (
        <Chart>
            <Chart.Legend>
                <LegendItem color="data1">{t('dashboard.inbox.graphSmartInbox')}</LegendItem>
                <LegendItem color="data2">{t('dashboard.inbox.graphOther')}</LegendItem>
            </Chart.Legend>
            <Chart.Content>
                <Chart.Scrollable>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={chartData} margin={{ top: 0, right: 20, left: 0, bottom: 10 }}>
                            <CartesianGrid vertical={false} className="stroke-line-weak" />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: 'inherit' }}
                                className="text-xs fill-content-medium"
                                tickMargin={10}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                className="text-xs fill-content-strong"
                                allowDecimals={false}
                                tickFormatter={(v) => v.toLocaleString(i18n.language)}
                            />
                            <RechartsTooltip
                                cursor={<Chart.TooltipCursor />}
                                content={({ payload }) => {
                                    const tooltipDate = payload?.[0]?.payload?.tooltipDate;
                                    const smartInbox = payload?.[0]?.payload?.smartInboxCount ?? 0;
                                    const other = payload?.[1]?.payload?.nonSmartInboxCount ?? 0;
                                    return (
                                        <TooltipContent>
                                            <TooltipContent.Title>{tooltipDate}</TooltipContent.Title>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data1">
                                                    {t('dashboard.inbox.graphSmartInbox')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {smartInbox.toLocaleString(i18n.language)}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data2">
                                                    {t('dashboard.inbox.graphOther')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {other.toLocaleString(i18n.language)}
                                                </span>
                                            </TooltipContent.Row>
                                        </TooltipContent>
                                    );
                                }}
                            />
                            <Bar dataKey="smartInboxCount" className="fill-data-data1" barSize={barSize} />
                            <Bar dataKey="nonSmartInboxCount" className="fill-data-data2" barSize={barSize} />
                        </BarChart>
                    </ResponsiveContainer>
                </Chart.Scrollable>
            </Chart.Content>
        </Chart>
    );
};

const DOCUMENT_TYPE_TRANSLATION_KEYS: Record<string, string> = {
    ORDER: 'dashboard.filters.documentType.order',
    ORDER_CONFIRMATION: 'dashboard.filters.documentType.orderConfirmation',
    INVOICE: 'dashboard.filters.documentType.invoice',
    DELIVERY_NOTE: 'dashboard.filters.documentType.deliveryNote',
    UNIVERSAL: 'dashboard.filters.documentType.universal',
    REQUEST_FOR_QUOTATION: 'dashboard.filters.documentType.rfq',
    UNCATEGORIZED: 'dashboard.filters.uncategorized',
};

const WORKIST_UNCATEGORIZED_NAME = 'UNCATEGORIZED';

type EmailCategory = {
    categoryName: string;
    count: number;
    percentage?: number;
    trendPercentagePoints?: number;
};

const TOP_CATEGORIES_LIMIT = 4;
const OTHERS_KEY = '__others__';
const UNCATEGORIZED_KEY = '__uncategorized__';

type OverTimeRow = {
    date: string;
    categoryName: string;
    count: number;
};

const EmailCategoriesSection = ({ integrationIds, dateRange }: { integrationIds: string[]; dateRange: string[] }) => {
    const { t, i18n } = useTranslation('analytics');
    const [mode, setMode] = useState<'accumulated' | 'over_time'>('accumulated');
    const [hoveredBarKey, setHoveredBarKey] = useState<string | null>(null);

    const accumulated = useSmartInboxEmailCategories({ integrationIds, dateRange });
    const overTime = useSmartInboxEmailCategoriesOverTime({ integrationIds, dateRange });

    const othersCssColor = '#818CF8';
    const uncategorizedCssColor = '#71717a';

    const resolveCategoryName = (cat: { categoryName: string }) => {
        const key = DOCUMENT_TYPE_TRANSLATION_KEYS[cat.categoryName];
        return key ? t(key) : cat.categoryName;
    };
    const isUncategorized = (cat: { categoryName: string }) => cat.categoryName === WORKIST_UNCATEGORIZED_NAME;

    // Accumulated
    const accAll = (accumulated.data as EmailCategory[]) || [];
    const accCategories = useMemo(
        () => accAll.filter((c) => !isUncategorized(c)).sort((a, b) => b.count - a.count),
        [accAll]
    );
    const accUncategorized = accAll.find(isUncategorized);
    const accGrandTotal = accAll.reduce((sum, c) => sum + c.count, 0);
    const accCategoryTotal = accCategories.reduce((sum, c) => sum + c.count, 0);

    // Overtime
    const isMonthly = useMemo(() => {
        const [start, end] = dateRange.map(safeParseDate);
        return start && end ? differenceInDays(end, start) > MONTHLY_THRESHOLD_DAYS : false;
    }, [dateRange]);

    const { chartData, overtimeTop4 } = useMemo(() => {
        const rows = (overTime.data || []) as OverTimeRow[];
        const overtimeTop4 = accCategories
            .slice(0, TOP_CATEGORIES_LIMIT)
            .map((cat, i) => ({ id: cat.categoryName, name: resolveCategoryName(cat), slot: `t${i}` }));
        const slotById = new Map(overtimeTop4.map((cat) => [cat.id, cat.slot]));

        const buckets = new Map<string, Record<string, unknown>>();

        if (isMonthly) {
            for (const key of getAllMonthsInRange(dateRange[0], dateRange[1])) {
                buckets.set(key, {
                    date: formatDate(i18n.language, key, true),
                    tooltipDate: formatDateFull(i18n.language, key, true),
                });
            }
        }

        for (const row of rows) {
            const parsed = safeParseDate(row.date);
            if (!parsed) {
                continue;
            }

            const bucketKey = isMonthly ? format(parsed, 'yyyy-MM') : row.date;

            if (!buckets.has(bucketKey)) {
                buckets.set(bucketKey, {
                    date: formatDate(i18n.language, bucketKey, isMonthly),
                    tooltipDate: formatDateFull(i18n.language, bucketKey, isMonthly),
                });
            }
            const bucket = buckets.get(bucketKey)!;

            if (isUncategorized(row)) {
                bucket[UNCATEGORIZED_KEY] = ((bucket[UNCATEGORIZED_KEY] as number) || 0) + row.count;
            } else {
                const slot = slotById.get(row.categoryName);
                if (slot) {
                    bucket[slot] = ((bucket[slot] as number) || 0) + row.count;
                } else {
                    bucket[OTHERS_KEY] = ((bucket[OTHERS_KEY] as number) || 0) + row.count;
                    const name = resolveCategoryName(row);
                    const breakdown = (bucket.__breakdown as { name: string; count: number }[]) || [];
                    const entry = breakdown.find((b) => b.name === name);

                    if (entry) {
                        entry.count += row.count;
                    } else {
                        breakdown.push({ name, count: row.count });
                    }
                    bucket.__breakdown = breakdown;
                }
            }
        }

        const chartData = [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, bucket]) => bucket);

        return { chartData, overtimeTop4 };
    }, [overTime.data, isMonthly, accCategories, i18n.language, dateRange]);

    const controls = (
        <FilterGroup>
            <FilterGroup.Button active={mode === 'accumulated'} onClick={() => setMode('accumulated')}>
                {t('dashboard.inbox.accumulated')}
            </FilterGroup.Button>
            <FilterGroup.Button active={mode === 'over_time'} onClick={() => setMode('over_time')}>
                {t('dashboard.inbox.overTime')}
            </FilterGroup.Button>
        </FilterGroup>
    );

    const CustomLegendDot = ({ color, label }: { color: string; label: string }) => (
        <div className="flex gap-2 items-center text-sm text-content-strong">
            <div className="w-3.5 h-3.5 rounded flex-shrink-0" style={{ background: color }} />
            <div>{label}</div>
        </div>
    );

    return (
        <ChartCard
            title={t('dashboard.inbox.emailCategories')}
            helpText={t('dashboard.inbox.emailCategoriesHelp')}
            loading={accumulated.loading}
            controls={controls}
            className="min-h-120"
        >
            {mode === 'over_time' ? (
                <Chart>
                    <Chart.Legend>
                        {overtimeTop4.map((cat, i) => (
                            <CustomLegendDot key={cat.id} color={DATA_CSS_COLORS[COLOR_KEYS[i]]} label={cat.name} />
                        ))}
                        {chartData.some((d) => (d[OTHERS_KEY] as number) > 0) && (
                            <CustomLegendDot color={othersCssColor} label={t('dashboard.inbox.otherCategories')} />
                        )}
                        {chartData.some((d) => (d[UNCATEGORIZED_KEY] as number) > 0) && (
                            <CustomLegendDot color={uncategorizedCssColor} label={t('dashboard.inbox.uncategorized')} />
                        )}
                    </Chart.Legend>
                    <Chart.Content>
                        <Chart.Scrollable>
                            <ResponsiveContainer width="100%" height={349}>
                                <BarChart data={chartData} margin={{ top: 0, right: 20, left: 0, bottom: 10 }}>
                                    <CartesianGrid vertical={false} className="stroke-line-weak" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fill: 'inherit' }}
                                        className="text-xs fill-content-medium"
                                        tickMargin={10}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        className="text-xs fill-content-strong"
                                        allowDecimals={false}
                                        tickFormatter={(v) => v.toLocaleString(i18n.language)}
                                    />
                                    <RechartsTooltip
                                        cursor={<Chart.TooltipCursor />}
                                        content={({ payload }) => {
                                            if (!payload?.length) return null;
                                            const row = payload[0].payload;
                                            if (!hoveredBarKey) {
                                                const bd = (row.__breakdown as { name: string; count: number }[]) || [];
                                                return (
                                                    <TooltipContent>
                                                        <TooltipContent.Title>
                                                            {row.tooltipDate as string}
                                                        </TooltipContent.Title>
                                                        {overtimeTop4.map((cat, i) => {
                                                            const count = row[cat.slot] as number | undefined;
                                                            if (!count) return null;
                                                            return (
                                                                <TooltipContent.Row key={cat.slot}>
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span
                                                                            className="w-2.5 h-2.5 rounded-sm flex-shrink-0 inline-block"
                                                                            style={{
                                                                                background:
                                                                                    DATA_CSS_COLORS[COLOR_KEYS[i]],
                                                                            }}
                                                                        />
                                                                        {cat.name}
                                                                    </span>
                                                                    <span className="font-medium">
                                                                        {count.toLocaleString(i18n.language)}
                                                                    </span>
                                                                </TooltipContent.Row>
                                                            );
                                                        })}
                                                        {(row[OTHERS_KEY] as number) > 0 && (
                                                            <>
                                                                <TooltipContent.Row>
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span
                                                                            className="w-2.5 h-2.5 rounded-sm flex-shrink-0 inline-block"
                                                                            style={{ background: othersCssColor }}
                                                                        />
                                                                        {t('dashboard.inbox.otherCategories')}
                                                                    </span>
                                                                    <span className="font-medium">
                                                                        {(row[OTHERS_KEY] as number).toLocaleString(
                                                                            i18n.language
                                                                        )}
                                                                    </span>
                                                                </TooltipContent.Row>
                                                                {bd.map((b) => (
                                                                    <TooltipContent.Row
                                                                        key={b.name}
                                                                        className="pl-4 opacity-80"
                                                                    >
                                                                        <span>{b.name}</span>
                                                                        <span className="font-medium">
                                                                            {b.count.toLocaleString(i18n.language)}
                                                                        </span>
                                                                    </TooltipContent.Row>
                                                                ))}
                                                            </>
                                                        )}
                                                        {(row[UNCATEGORIZED_KEY] as number) > 0 && (
                                                            <TooltipContent.Row>
                                                                <span className="flex items-center gap-1.5">
                                                                    <span
                                                                        className="w-2.5 h-2.5 rounded-sm flex-shrink-0 inline-block"
                                                                        style={{ background: uncategorizedCssColor }}
                                                                    />
                                                                    {t('dashboard.inbox.uncategorized')}
                                                                </span>
                                                                <span className="font-medium">
                                                                    {(row[UNCATEGORIZED_KEY] as number).toLocaleString(
                                                                        i18n.language
                                                                    )}
                                                                </span>
                                                            </TooltipContent.Row>
                                                        )}
                                                    </TooltipContent>
                                                );
                                            }
                                            const i = overtimeTop4.findIndex((c) => c.slot === hoveredBarKey);
                                            if (i >= 0) {
                                                return (
                                                    <TooltipContent>
                                                        <TooltipContent.Title>
                                                            {row.tooltipDate as string}
                                                        </TooltipContent.Title>
                                                        <TooltipContent.Row>
                                                            <span className="flex items-center gap-1.5">
                                                                <span
                                                                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0 inline-block"
                                                                    style={{
                                                                        background: DATA_CSS_COLORS[COLOR_KEYS[i]],
                                                                    }}
                                                                />
                                                                {overtimeTop4[i].name}
                                                            </span>
                                                            <span className="font-medium">
                                                                {(row[hoveredBarKey] as number).toLocaleString(
                                                                    i18n.language
                                                                )}
                                                            </span>
                                                        </TooltipContent.Row>
                                                    </TooltipContent>
                                                );
                                            }
                                            if (hoveredBarKey === OTHERS_KEY) {
                                                const bd = (row.__breakdown as { name: string; count: number }[]) || [];
                                                return (
                                                    <TooltipContent>
                                                        <TooltipContent.Title>
                                                            {row.tooltipDate as string}
                                                        </TooltipContent.Title>
                                                        <TooltipContent.Row>
                                                            <span className="flex items-center gap-1.5">
                                                                <span
                                                                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0 inline-block"
                                                                    style={{ background: othersCssColor }}
                                                                />
                                                                {t('dashboard.inbox.otherCategories')}
                                                            </span>
                                                            <span className="font-medium">
                                                                {(row[OTHERS_KEY] as number).toLocaleString(
                                                                    i18n.language
                                                                )}
                                                            </span>
                                                        </TooltipContent.Row>
                                                        {bd.map((b) => (
                                                            <TooltipContent.Row
                                                                key={b.name}
                                                                className="pl-4 opacity-80"
                                                            >
                                                                <span>{b.name}</span>
                                                                <span className="font-medium">
                                                                    {b.count.toLocaleString(i18n.language)}
                                                                </span>
                                                            </TooltipContent.Row>
                                                        ))}
                                                    </TooltipContent>
                                                );
                                            }
                                            if (hoveredBarKey === UNCATEGORIZED_KEY) {
                                                return (
                                                    <TooltipContent>
                                                        <TooltipContent.Title>
                                                            {row.tooltipDate as string}
                                                        </TooltipContent.Title>
                                                        <TooltipContent.Row>
                                                            <span className="flex items-center gap-1.5">
                                                                <span
                                                                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0 inline-block"
                                                                    style={{ background: uncategorizedCssColor }}
                                                                />
                                                                {t('dashboard.inbox.uncategorized')}
                                                            </span>
                                                            <span className="font-medium">
                                                                {(row[UNCATEGORIZED_KEY] as number).toLocaleString(
                                                                    i18n.language
                                                                )}
                                                            </span>
                                                        </TooltipContent.Row>
                                                    </TooltipContent>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    {overtimeTop4.map((cat, i) => (
                                        <Bar
                                            key={cat.slot}
                                            dataKey={cat.slot}
                                            stackId="a"
                                            className="cursor-pointer"
                                            fill={DATA_CSS_COLORS[COLOR_KEYS[i]]}
                                            opacity={!hoveredBarKey || hoveredBarKey === cat.slot ? 1 : 0.4}
                                            barSize={28}
                                            onMouseEnter={() => setHoveredBarKey(cat.slot)}
                                            onMouseLeave={() => setHoveredBarKey(null)}
                                        />
                                    ))}
                                    <Bar
                                        dataKey={OTHERS_KEY}
                                        stackId="a"
                                        className="cursor-pointer"
                                        fill={othersCssColor}
                                        opacity={!hoveredBarKey || hoveredBarKey === OTHERS_KEY ? 1 : 0.4}
                                        barSize={28}
                                        onMouseEnter={() => setHoveredBarKey(OTHERS_KEY)}
                                        onMouseLeave={() => setHoveredBarKey(null)}
                                    />
                                    <Bar
                                        dataKey={UNCATEGORIZED_KEY}
                                        stackId="a"
                                        className="cursor-pointer"
                                        fill={uncategorizedCssColor}
                                        opacity={!hoveredBarKey || hoveredBarKey === UNCATEGORIZED_KEY ? 1 : 0.4}
                                        barSize={28}
                                        onMouseEnter={() => setHoveredBarKey(UNCATEGORIZED_KEY)}
                                        onMouseLeave={() => setHoveredBarKey(null)}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Chart.Scrollable>
                    </Chart.Content>
                </Chart>
            ) : (
                <div className="flex flex-col gap-4 w-full">
                    {accGrandTotal > 0 && (
                        <div className="flex gap-0.5 h-4 rounded overflow-hidden">
                            {accCategories.map((cat, i) => (
                                <div
                                    key={cat.categoryName}
                                    className="h-4"
                                    style={{ width: `${cat.percentage ?? 0}%`, background: generateCategoryColor(i) }}
                                />
                            ))}
                            {accUncategorized && (
                                <div
                                    className="h-4"
                                    style={{
                                        width: `${accUncategorized.percentage ?? 0}%`,
                                        background: uncategorizedCssColor,
                                    }}
                                />
                            )}
                        </div>
                    )}
                    <Stats>
                        <Stats.Group>
                            {accCategories.map((cat, i) => (
                                <Stats.Row key={cat.categoryName}>
                                    <Stats.Label>
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                                style={{ background: generateCategoryColor(i) }}
                                            />
                                            {resolveCategoryName(cat)}
                                        </div>
                                    </Stats.Label>
                                    <Stats.Value count={cat.count} totalCount={accGrandTotal} />
                                </Stats.Row>
                            ))}
                            <Stats.Divider />
                            <Stats.Row>
                                <Stats.Label>
                                    <span className="text-content-medium">{t('dashboard.inbox.total')}</span>
                                </Stats.Label>
                                <Stats.Value count={accCategoryTotal} totalCount={accGrandTotal} />
                            </Stats.Row>
                        </Stats.Group>
                        {accUncategorized && (
                            <>
                                <Stats.Group>
                                    <Stats.Row>
                                        <Stats.Label>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-sm flex-shrink-0"
                                                    style={{ background: uncategorizedCssColor }}
                                                />
                                                {t('dashboard.inbox.uncategorized')}
                                            </div>
                                        </Stats.Label>
                                        <Stats.Value count={accUncategorized.count} totalCount={accGrandTotal} />
                                    </Stats.Row>
                                </Stats.Group>
                                <Stats.Group className="bg-surface-transparent py-1">
                                    <Stats.Row>
                                        <Stats.Label>{t('dashboard.inbox.totalEmails')}</Stats.Label>
                                        <Stats.Value
                                            count={accUncategorized.count + accCategoryTotal}
                                            totalCount={accGrandTotal}
                                        />
                                    </Stats.Row>
                                </Stats.Group>
                            </>
                        )}
                    </Stats>
                </div>
            )}
        </ChartCard>
    );
};

type RateData = { count: number; totalCount: number; rate: number; trendPercentagePoints: number } | null;

const SmartInboxPerformanceSection = ({
    integrationIds,
    dateRange,
    integrationOptions,
    selectedIntegrationIds,
    onSelectedIntegrationIdsChange,
}: {
    integrationIds: string[];
    dateRange: string[];
    integrationOptions: { label: string; value: string; groupName: string }[];
    selectedIntegrationIds: string[];
    onSelectedIntegrationIdsChange: (ids: string[]) => void;
}) => {
    const { t } = useTranslation('analytics');

    const { data: performanceMetrics, loading: performanceMetricsLoading } = useSmartInboxPerformanceMetrics({
        integrationIds,
        dateRange,
    });

    const renderRate = (data: RateData, descriptionKey: string, invertedTrend = false) => {
        if (!data)
            return {
                value: '-',
                trend: undefined,
                trendDirection: undefined,
                trendPositive: undefined,
                description: undefined,
            };
        const trend = formatTrend(data.trendPercentagePoints, invertedTrend);
        return {
            value: Math.round(data.rate),
            trend: trend?.label,
            trendDirection: trend?.direction,
            trendPositive: trend?.positive,
            description: (
                <Trans
                    t={t}
                    i18nKey={descriptionKey}
                    count={data.count}
                    values={{ total: data.totalCount }}
                    components={{ strong: <strong /> }}
                />
            ),
        };
    };

    const e2eProps = renderRate(performanceMetrics?.automationRate as RateData, 'dashboard.inbox.e2eDescription');
    const recognitionProps = renderRate(
        performanceMetrics?.recognitionRate as RateData,
        'dashboard.inbox.recognitionDescription'
    );
    const manualForwardingProps = renderRate(
        performanceMetrics?.manualForwardingRate as RateData,
        'dashboard.inbox.manualForwardingDescription',
        true
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
                <h3 className="font-medium shrink-0 text-sm text-content-strong">
                    {t('dashboard.inbox.smartInboxPerformance')}
                </h3>
                <div className="flex-1 border-t border-line-weak"></div>
                <FilterDropdown
                    label={t('dashboard.inbox.smartInboxes', { total: selectedIntegrationIds.length })}
                    allLabel={t('dashboard.inbox.smartInboxes', { total: selectedIntegrationIds.length })}
                    formatLabel={() => t('dashboard.inbox.smartInboxes', { total: selectedIntegrationIds.length })}
                    options={integrationOptions}
                    selected={selectedIntegrationIds}
                    onSelectedChange={onSelectedIntegrationIdsChange}
                    renderGroupName={(groupName) =>
                        groupName === 'personal'
                            ? t('dashboard.inbox.personalSmartInbox')
                            : t('dashboard.inbox.sharedSmartInbox')
                    }
                    multiple
                    dropdownClassname={'mr-6.5'}
                    showSearch
                    active={selectedIntegrationIds.length < integrationOptions.length}
                />
            </div>
            <div className="flex flex-row gap-4">
                <div className="grid grid-cols-1 w-1/4 gap-4">
                    <PerformanceMetricCard
                        title={t('dashboard.inbox.recognitionRate')}
                        helpText={t('dashboard.inbox.recognitionRateHelp')}
                        loading={performanceMetricsLoading}
                        value={recognitionProps.value}
                        unit="%"
                        trend={recognitionProps.trend}
                        trendDirection={recognitionProps.trendDirection}
                        trendPositive={recognitionProps.trendPositive}
                        description={recognitionProps.description}
                    />
                    <PerformanceMetricCard
                        title={t('dashboard.inbox.automationEntToEnd')}
                        helpText={t('dashboard.inbox.automationEndToEndHelp')}
                        loading={performanceMetricsLoading}
                        value={e2eProps.value}
                        unit="%"
                        trend={e2eProps.trend}
                        trendDirection={e2eProps.trendDirection}
                        trendPositive={e2eProps.trendPositive}
                        description={e2eProps.description}
                    />
                    <PerformanceMetricCard
                        title={t('dashboard.inbox.manualForwarding')}
                        helpText={t('dashboard.inbox.manualForwardingHelp')}
                        loading={performanceMetricsLoading}
                        value={manualForwardingProps.value}
                        unit="%"
                        trend={manualForwardingProps.trend}
                        trendDirection={manualForwardingProps.trendDirection}
                        trendPositive={manualForwardingProps.trendPositive}
                        description={manualForwardingProps.description}
                    />
                </div>

                <div className="grid w-3/4">
                    <EmailCategoriesSection integrationIds={selectedIntegrationIds} dateRange={dateRange} />
                </div>
            </div>
            <TopEmailCategoriesSection integrationIds={selectedIntegrationIds} dateRange={dateRange} />
        </div>
    );
};

const TOP_EMAIL_CATEGORIES_LIMIT = 5;

const TopEmailCategoriesSection = ({
    integrationIds,
    dateRange,
}: {
    integrationIds: string[];
    dateRange: string[];
}) => {
    const { t } = useTranslation('analytics');
    const { data, loading } = useSmartInboxTopEmailCategories({
        integrationIds,
        dateRange,
        limit: TOP_EMAIL_CATEGORIES_LIMIT,
    });
    const categories = (data as EmailCategory[]) || [];

    const resolveCategoryName = (cat: { categoryName: string }) => {
        const key = DOCUMENT_TYPE_TRANSLATION_KEYS[cat.categoryName];
        return key ? t(key) : cat.categoryName;
    };

    return (
        <ChartCard
            title={t('dashboard.inbox.topEmailCategories', { total: categories?.length })}
            helpText={t('dashboard.inbox.topEmailCategoriesHelp', { total: categories?.length })}
            loading={loading}
        >
            <div className="flex flex-col gap-5 w-full py-2">
                {categories.map((cat) => {
                    const trend = formatTrend(cat.trendPercentagePoints);
                    return (
                        <div key={cat.categoryName} className="flex items-center gap-4">
                            <span className="w-44 shrink-0 text-sm text-content-strong truncate">
                                {resolveCategoryName(cat)}
                            </span>
                            <div className="flex flex-1 items-center gap-2">
                                <div
                                    className="h-10 bg-[var(--color-data-data1)] shrink-0"
                                    style={{ width: `${cat.percentage ?? 0}%` }}
                                />
                                <span className="text-sm text-content-medium shrink-0">
                                    {Math.round(cat.percentage ?? 0)}%
                                </span>
                            </div>
                            {trend && (
                                <div className="flex items-center gap-1.5 text-sm shrink-0 text-content-medium">
                                    <div className="p-1 bg-surface-weak-default text-content-strong">
                                        {trend.direction === 'up' ? (
                                            <TrendUpIcon className="text-content-strong" />
                                        ) : (
                                            <TrendDownIcon className="text-content-strong" />
                                        )}
                                        <span className={classnames('pl-2 text-content-strong')}>{trend.label}</span>
                                    </div>
                                    <span>{t('dashboard.inbox.vsSelected')}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ChartCard>
    );
};

const InboxAnalyticsContent = ({
    dateRange,
    integrationOptions,
    allIntegrationIds,
}: {
    dateRange: string[];
    integrationOptions: SmartInboxIntegrationOption[];
    allIntegrationIds: string[];
}) => {
    const { t } = useTranslation('analytics');

    const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<string[]>([]);

    useEffect(() => {
        if (allIntegrationIds.length > 0) {
            setSelectedIntegrationIds(allIntegrationIds);
        }
    }, [allIntegrationIds.join(',')]);

    const share = useSmartInboxShare({ dateRange });
    const shareOverTime = useSmartInboxShareOverTime({ dateRange });

    const totalInboxVolume = share.data?.totalCount ?? '-';
    const smartInboxPct = share.data?.smartInboxPercentage != null ? Math.round(share.data.smartInboxPercentage) : '-';

    const totalVolumeTrend = formatTrend(share.data?.totalCountTrendPercentage);
    const shareTrend = formatTrend(share.data?.trendPercentagePoints);

    return (
        <div className="flex flex-col gap-4 justify-start overflow-x-hidden p-7 pt-0">
            <div className="flex gap-4 w-full min-h-[20rem] flex-row 3xl:flex-row">
                <div className="w-3/4 3xl:w-2/3 min-w-0">
                    <ChartCard
                        title={t('dashboard.inbox.emailDistribution')}
                        helpText={t('dashboard.inbox.emailDistributionHelp')}
                        loading={shareOverTime.loading}
                        className="h-full min-h-120"
                    >
                        <EmailDistributionChart data={shareOverTime.data} dateRange={dateRange} />
                    </ChartCard>
                </div>
                <div className="grid w-1/4 gap-4 flex-row grid-cols-1 3xl:grid-cols-1 3xl:w-1/3 min-w-0">
                    <MetricCard
                        title={t('dashboard.inbox.totalInboxVolume')}
                        helpText={t('dashboard.inbox.totalInboxVolumeHelp')}
                        loading={share.loading}
                        value={totalInboxVolume}
                        trend={totalVolumeTrend?.label}
                        trendDirection={totalVolumeTrend?.direction}
                        trendPositive={totalVolumeTrend?.positive}
                    />
                    <MetricCard
                        title={t('dashboard.inbox.smartInboxShare')}
                        helpText={t('dashboard.inbox.smartInboxShareHelp')}
                        loading={share.loading}
                        value={smartInboxPct}
                        unit="%"
                        trend={shareTrend?.label}
                        trendDirection={shareTrend?.direction}
                        trendPositive={shareTrend?.positive}
                    />
                </div>
            </div>

            <SmartInboxPerformanceSection
                integrationIds={selectedIntegrationIds}
                dateRange={dateRange}
                integrationOptions={integrationOptions}
                selectedIntegrationIds={selectedIntegrationIds}
                onSelectedIntegrationIdsChange={setSelectedIntegrationIds}
            />
        </div>
    );
};

export default InboxAnalyticsContent;
